import { Box, Button, Col, Modal, Row, Text, TextInput } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import { stringToUuid } from '@common/utils/uuid';
import type { IServiceOrganizationConsumer } from '../../../../../interfaces/serviceOrganizationConsumer';
import { translateKey } from '../../../../../../translate';
import { SortDirection } from '../../../../../../utils/sortDirection';
import { BadgeWithLabel } from '../../../../../elements/BadgeWithLabel';
import { ListPager } from '../../../../../elements/ListPager';
import { ConfirmationModal } from '../../../../../elements/modals/ConfirmationModal';
import { findServiceConsumerOrganizations } from '../../personalSettingsPreferenceListHelper';
import { PersonalSettingsPreferenceListExaminationConsumersTable } from '../PreferenceListExaminationTables/PersonalSettingsPreferenceListExaminationConsumersTable';

const defaultItemsPerPage = 8;

interface IPersonalSettingsPreferenceListExaminationServiceConsumerSearchNationWideModalProps {
    consumerOrganizationIdsToAdd?: string[];
    consumerOrganizationIdsToExclude?: string[];
    isOpen: boolean;
    isReadOnly?: boolean;
    itemsPerPage?: number;
    onAddOrganizations: (
        serviceOrganizationConsumerOrganizationIds: string[],
        serviceOrganizationConsumers?: IServiceOrganizationConsumer[]
    ) => Promise<void>;
    onClose: () => Promise<void>;
    providerId?: string;
    selectedServiceOrganizationConsumersOrganizationIds?: string[];
    serviceId: string;
}

export function PersonalSettingsPreferenceListExaminationServiceConsumerSearchNationWideModal(
    props: IPersonalSettingsPreferenceListExaminationServiceConsumerSearchNationWideModalProps
) {
    const [serviceOrganizationConsumers, setServiceOrganizationConsumers] = useState<IServiceOrganizationConsumer[]>(
        []
    );
    const [serviceOrganizationConsumersTotal, setserviceOrganizationConsumersTotal] = useState(0);
    const [selectedServiceOrganizationConsumerOrganizationIds, setSelectedServiceOrganizationConsumerOrganizationIds] =
        useState<string[]>([]);
    const [selectedServiceOrganizationConsumerOrganizations, setSelectedServiceOrganizationConsumerOrganizations] =
        useState<IServiceOrganizationConsumer[]>([]);

    const [searchOnCityZipCodeOrState, setSearchOnCityZipCodeOrState] = useState('');
    const [searchOnNameOrOrganization, setSearchOnNameOrOrganization] = useState('');
    const [page, setPage] = useState(1);

    const [isSearching, setIsSearching] = useState(false);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);

    const [sortKey, setSortKey] = useState('organization.name');
    const [sortDirection, setSortDirection] = useState(SortDirection.asc);

    useEffect(() => {
        setSearchOnCityZipCodeOrState('');
        setSearchOnNameOrOrganization('');
        setPage(1);
        search();
        setSelectedServiceOrganizationConsumerOrganizationIds([]);
        setSelectedServiceOrganizationConsumerOrganizations([]);
    }, [props.serviceId, props.providerId, props.onClose]);

    useEffect(() => {
        search();
    }, [searchOnCityZipCodeOrState, searchOnNameOrOrganization, sortKey, sortDirection, page]);

    return (
        <>
            <ConfirmationModal
                handleNo={() => setShowConfirmationModal(false)}
                handleYes={() => {
                    setShowConfirmationModal(false);
                    props.onClose();
                }}
                isOpen={showConfirmationModal}
                question={translateKey('personalSettings.preferenceList.closeConfirmationModalMessage')}
                title={translateKey('personalSettings.preferenceList.closeConfirmationModalTitle')}
            />
            <Modal
                isOpen={props.isOpen}
                onClose={handleCloseModal}
                size="large"
            >
                <Modal.Header>{translateKey('personalSettings.preferenceList.searchNationWide')}</Modal.Header>
                <Modal.CloseButton></Modal.CloseButton>
                <Modal.Body>
                    <Box
                        mb={6}
                        mt={4}
                    >
                        <Row
                            align="center"
                            rowGap={2}
                        >
                            <Col span={[12, null, 5]}>
                                {renderSearchTextInput(
                                    'searchOnNameOrOrganization',
                                    searchOnNameOrOrganization,
                                    translateKey('personalSettings.preferenceList.searchOnNameOrOrganization'),
                                    (text) => setSearchOnNameOrOrganization(text)
                                )}
                            </Col>
                            <Col span={[12, null, 2]}>
                                <Text
                                    as="small"
                                    key="andOr"
                                    size="xs"
                                    textAlign="center"
                                >
                                    {translateKey('common.andOr')}
                                </Text>
                            </Col>
                            <Col span={[12, null, 5]}>
                                {renderSearchTextInput(
                                    'searchOnCityZipCodeOrState',
                                    searchOnCityZipCodeOrState,
                                    translateKey('personalSettings.preferenceList.searchOnCityZipCodeOrState'),
                                    (text) => setSearchOnCityZipCodeOrState(text)
                                )}
                            </Col>
                        </Row>
                    </Box>

                    <PersonalSettingsPreferenceListExaminationConsumersTable
                        isReadOnly={props.isReadOnly}
                        onChange={async (value) => handleSelectedOrganizationIdsChange(value)}
                        onSort={handleSort}
                        selectedServiceOrganizationConsumersOrganizationIds={getSelectedOrganizationIdsOnPage()}
                        serviceOrganizationConsumers={serviceOrganizationConsumers}
                        sortDirection={sortDirection}
                        sortKey={sortKey}
                    />

                    <ListPager
                        isDisabled={isSearching}
                        itemsPerPage={defaultItemsPerPage}
                        onChangePage={handleNavigation}
                        page={page}
                        showNoResultsText
                        total={serviceOrganizationConsumersTotal}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <BadgeWithLabel
                        color="neutral"
                        label={translateKey('common.isSelected')}
                        value={selectedServiceOrganizationConsumerOrganizationIds.length.toString()}
                    />
                    <Button
                        isDisabled={selectedServiceOrganizationConsumerOrganizationIds.length === 0}
                        onClick={async () =>
                            await props.onAddOrganizations(
                                selectedServiceOrganizationConsumerOrganizationIds,
                                selectedServiceOrganizationConsumerOrganizations
                            )
                        }
                        variant="solid"
                    >
                        {translateKey('personalSettings.preferenceList.addOrganizations')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );

    function handleCloseModal() {
        if (selectedServiceOrganizationConsumerOrganizationIds.length) {
            setShowConfirmationModal(true);

            return;
        }
        props.onClose();
    }

    async function handleSort(newSortKey: string, newSortDirection: SortDirection) {
        setPage(1);
        setSortKey(newSortKey);
        setSortDirection(newSortDirection);
    }

    async function search() {
        setIsSearching(true);

        const result = await findServiceConsumerOrganizations({
            serviceId: props.serviceId,
            providerId: props.providerId,
            page,
            limit: defaultItemsPerPage,
            cityZipCodeOrStateSearchText: searchOnCityZipCodeOrState,
            serviceConsumerNameOrOrganizationSearchText: searchOnNameOrOrganization,
            sortKey,
            sortDirection,
        });
        const serviceOrganizationConsumersFromResult = result.serviceOrganizationConsumers.filter(
            (serviceOrganizationConsumer) =>
                !(props.selectedServiceOrganizationConsumersOrganizationIds ?? []).includes(
                    serviceOrganizationConsumer.organization.id
                )
        );
        setServiceOrganizationConsumers(serviceOrganizationConsumersFromResult);
        setserviceOrganizationConsumersTotal(serviceOrganizationConsumersFromResult.length);

        setIsSearching(false);
    }

    async function handleSearch() {
        setPage(1);
    }

    async function handleNavigation(newPage: number) {
        setPage(newPage);
    }

    function renderSearchTextInput(
        key: string,
        searchText: string,
        placeholder: string,
        onChange: (text: string) => void
    ) {
        return (
            <TextInput
                className="no-ms-clear"
                key={key}
                onChange={(e) => onChange(e.target.value)}
                onKeyPress={async (event) => {
                    if (event.key === 'Enter') {
                        event.preventDefault();
                        await handleSearch();
                    }
                }}
                onReset={() => onChange('')}
                placeholder={placeholder}
                suffix={{
                    icon: 'search',
                    labelText: 'search',
                    onSuffixClick: () => handleSearch(),
                }}
                value={searchText}
            />
        );
    }

    function getSelectedOrganizationIdsOnPage() {
        const serviceOrganizationConsumerIds = serviceOrganizationConsumers.map((consumer) => consumer.organization.id);

        return selectedServiceOrganizationConsumerOrganizationIds.filter((id) =>
            serviceOrganizationConsumerIds.includes(stringToUuid(id))
        );
    }

    function handleSelectedOrganizationIdsChange(selectedOrganizationIds: string[]) {
        const organizationIds = serviceOrganizationConsumers.map((consumer) => consumer.organization.id);
        let newSelectedOrganizationIds = selectedServiceOrganizationConsumerOrganizationIds.filter(
            (id) => !organizationIds.includes(stringToUuid(id))
        );
        if (selectedOrganizationIds.length > 0) {
            newSelectedOrganizationIds = [...newSelectedOrganizationIds, ...selectedOrganizationIds];
        }
        setSelectedServiceOrganizationConsumerOrganizationIds(newSelectedOrganizationIds);
        const selectedOrganizations = serviceOrganizationConsumers.filter((consumer) =>
            newSelectedOrganizationIds.includes(consumer.organization.id)
        );
        setSelectedServiceOrganizationConsumerOrganizations(selectedOrganizations);
    }
}
